import { inject, Injectable } from '@angular/core';

import { WindowService } from 'src/shared/providers/window.service';
import { PosthogService } from 'src/shared/services/posthog.service';

import { FEATURE_FLAGS, FEATURE_FLAG_DEFAULTS } from 'src/shared/constants/feature-flags';

import { environment } from 'src/environments/environment';

/**
 * PostHog "pm-demo-tour" experiment: whether a PM demo visitor gets the guided walkthrough.
 * Variant `guided-tour` shows DemoTourService's guide dock, which walks them through all five
 * demo pages once the welcome card is dismissed; `control` is the demo as it is today, with no
 * walkthrough.
 *
 * DESKTOP ONLY. The dock is a desktop experience, so mobile visitors are never enrolled:
 * resolveVariant() short-circuits before touching PostHog, so no flag is evaluated, no exposure
 * fires and nothing is cached. For the same reason an unresolved flag (PostHog blocked, flag off)
 * returns null instead of falling back to 'control' — a visitor who wasn't assigned a variant
 * must not count as a control participant. Both cases simply see no tour.
 *
 * Exposure ($feature_flag_called) is recorded at demo login (recordExposure(), from
 * DemoAccountService.loginToDemoAccount) — the same entry point as the other PM-demo
 * experiments, so both arms enroll at the identical moment, before the tour would appear.
 * KPI = scheduling a setup call (viewed_schedule_call_page → scheduled_call). Mirrors
 * PmScheduleCallFirstService: anonymous per-device bucketing, sticky cache, no-exposure read;
 * prod vs sandbox by getFlagKey(); flags carry NO targeting.
 */
@Injectable({ providedIn: 'root' })
export class PmDemoTourService {

  private posthogService = inject(PosthogService);
  private windowService = inject(WindowService);

  private getFlagKey(): string {
    return environment.environment === 'production'
      ? FEATURE_FLAGS.PM_DEMO_TOUR
      : FEATURE_FLAGS.PM_DEMO_TOUR_SANDBOX;
  }

  /**
   * True when this visitor is in the arm that gets the walkthrough. Reads the sticky cache
   * (warmed by recordExposure() at demo login), so it is safe to call from a getter.
   */
  isTourVariant(): boolean {
    const flagKey = this.getFlagKey();
    const variant = localStorage.getItem(`posthog_variant_${flagKey}`)
      || (FEATURE_FLAG_DEFAULTS[flagKey] as string);
    return variant === 'guided-tour';
  }

  /** Record experiment exposure ($feature_flag_called) — call at demo login. */
  async recordExposure(): Promise<void> {
    const variant = await this.resolveVariant();
    if (!variant) {
      return;
    }
    this.posthogService.capture('$feature_flag_called', {
      $feature_flag: this.getFlagKey(),
      $feature_flag_response: variant,
    });
  }

  /** The assigned variant, or null when this visitor is not in the experiment at all. */
  async resolveVariant(): Promise<string | null> {
    // Desktop only: don't even evaluate the flag on mobile, so no variant is ever assigned.
    if (!this.windowService.isDesktopRes) {
      return null;
    }

    const flagKey = this.getFlagKey();
    const cacheKey = `posthog_variant_${flagKey}`;

    const cached = localStorage.getItem(cacheKey);
    if (cached) {
      return cached;
    }

    await this.posthogService.waitForIdentification();
    await this.posthogService.waitForFlags();
    const raw = await this.posthogService.getFeatureFlagNoExposure(flagKey);
    // null/false (flag off, PostHog unavailable, targeting miss) is a non-assignment: return
    // null rather than caching a 'control' the visitor was never actually bucketed into.
    if (raw === null || raw === undefined || raw === false) {
      return null;
    }

    localStorage.setItem(cacheKey, raw as string);
    return raw as string;
  }
}
